import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from "rxjs";
import {auditTime, debounceTime, filter} from "rxjs/operators";
import {DetectSetterChanges, isValidNumber, isVoid} from "../../utils";
import {MessageBusService} from "../../message-bus.service";
import {ZonesGridColumn, ZonesGridComponent} from "../zones-grid/zones-grid.component";
import {IvCacheService} from "../../iv-cache.service";

@Component({
    selector: 'ets-volatility-header',
    template: `
      <dx-load-indicator width="18" height="18" *ngIf="this.loading"></dx-load-indicator>
      <div class="volatility" *ngIf="!this.loading || this.volatility != null">
        IV={{this.volatility | percent:'1.1-1'}}
      </div>
    `,
    styles: [
        `
        .volatility {
            font-size: 85%;
        }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class VolatilityHeaderComponent implements OnInit, OnDestroy {
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _messageBus: MessageBusService,
        private readonly _ivCacheService: IvCacheService
    ) {
    }

    private static readonly GATE_TIMEOUT_MS = 10000;

    @Input() underlying: string;

    @Input() expiration: string;

    @Input() root: { staticVol: number };

    private _volatility: number;
    get volatility(): number {
        return this.root.staticVol|| this._volatility;
    }

    @DetectSetterChanges()
    set volatility(v: number) {
        this._volatility = v;
    }

    private _qgCursorDate: string = null;
    private _subscriptions: Subscription[] = [];
    private _ivRefreshTimerSub: Subscription = null;
    private _ivLoadedOnce = false;
    private _gated = false;
    private _gateTimeout: any = null;
    private _isQgHistoryMode = false;

    private _loading = true;
    get loading(): boolean {
        return this._loading;
    }

    @DetectSetterChanges()
    set loading(v: boolean) {
        this._loading = v;
    }

    ngOnInit() {
        this._subscriptions.push(
            this._messageBus.of<any>('Qg.CursorDateChangedMessage')
                .subscribe(msg => {
                    const cursorDate = msg.payload?.cursorDate;
                    if (isVoid(cursorDate)) {
                        this._qgCursorDate = null;
                        return;
                    }
                    const newDate = cursorDate.split('T')[0];
                    if (this._qgCursorDate === newDate) {
                        return;
                    }
                    this._qgCursorDate = newDate;
                    if (!this._gated) { this.fetchIv(); }
                })
        );

        this._subscriptions.push(
            this._messageBus.of<any>('AtmStrikeChangedEvent')
                .pipe(debounceTime(2000))
                .subscribe(() => { if (!this._gated) { this.fetchIv(); } })
        );

        this._subscriptions.push(
            this._messageBus.of<any>('Qg.StreamModeChanged')
                .subscribe(msg => {
                    this._isQgHistoryMode = msg.payload?.mode === 'History';
                    this.closeGate();
                    this.recreateQuoteSub();
                })
        );

        this.recreateQuoteSub();
        this.fetchIv();
    }

    ngOnDestroy() {
        if (this._ivRefreshTimerSub) {
            this._ivRefreshTimerSub.unsubscribe();
            this._ivRefreshTimerSub = null;
        }
        if (this._gateTimeout) {
            clearTimeout(this._gateTimeout);
            this._gateTimeout = null;
        }
        this._subscriptions.forEach(s => s.unsubscribe());
        this._subscriptions = [];
    }

    private closeGate(): void {
        this._gated = true;
        if (this._gateTimeout) { clearTimeout(this._gateTimeout); }
        this._gateTimeout = setTimeout(() => {
            this._gated = false;
            this._gateTimeout = null;
            this.fetchIv();
        }, VolatilityHeaderComponent.GATE_TIMEOUT_MS);
    }

    private openGate(): void {
        this._gated = false;
        if (this._gateTimeout) {
            clearTimeout(this._gateTimeout);
            this._gateTimeout = null;
        }
    }

    private recreateQuoteSub(): void {
        if (this._ivRefreshTimerSub) {
            this._ivRefreshTimerSub.unsubscribe();
            this._ivRefreshTimerSub = null;
        }
        const timeOp = this._isQgHistoryMode
            ? debounceTime(2000)
            : auditTime(2000);
        this._ivRefreshTimerSub = this._messageBus.of<any>('QuoteDto')
            .pipe(
                filter(msg => msg.payload.some((q: any) => q.ticker === this.underlying)),
                timeOp
            )
            .subscribe(() => {
                this.openGate();
                this.fetchIv();
            });
    }

    private async fetchIv(): Promise<void> {
        const hasValue = this._ivLoadedOnce || isValidNumber(this._volatility) || !!this.root?.staticVol;
        this.loading = !hasValue;

        try {
            // Routed through IvCacheService so HM cells + OPG header + this APG header
            // sharing the same (underlying, expiration) coalesce to one HTTP per TTL.
            const reply = await this._ivCacheService.getIv(this.underlying, this.expiration);
            this.volatility = reply.volatility;
            if (isValidNumber(reply.volatility)) {
                this._ivLoadedOnce = true;
            }
        } catch {
            if (!isValidNumber(this._volatility)) {
                this.volatility = NaN;
            }
        } finally {
            this.loading = false;
        }
    }
}
